import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { AxiosResponse } from "axios";
import React, { useEffect, useRef, useState } from "react";
import Select from "react-select";
import Row from "./Row";
import FacilityService from "Services/FacilityService/FacilityService";
import {
  FileTemplateLookup,
  GetAllTemplateSettings,
  TranslatorLookupApi,
} from "Services/PGXLis/ResultFilePreConfiguration";
import { Loader } from "Shared/Common/Loader";
import NoRecord from "Shared/Common/NoRecord";
import PermissionComponent, {
  getPermissionDisplayName,
} from "Shared/Common/Permissions/PermissionComponent";
import useIsMobile from "Shared/hooks/useIsMobile";
import useLang from "Shared/hooks/useLanguage";
import usePagination from "Shared/hooks/usePagination";
import { ArrowDown, ArrowUp } from "Shared/Icons";
import CustomPagination from "Shared/JsxPagination";
import { StringRecord } from "Shared/Type";
import { reactSelectSMStyle, styles } from "Utils/Common";
import { SortingTypeI } from "Utils/consts";

export interface IRows {
  labId: number;
  templateName: string;
  rowStatus: boolean | undefined;
  templateId: number;
  translatorId?: string;
  isActive?: boolean;
  labName?: string;
  translatorName?: string;
  cells?: any[];
  reqTypeId?: number;
}

interface DropDownValues {
  translatorLookup: Array<{
    value: number;
    label: string;
  }>;
  referenceLab: Array<{
    value: number;
    label: string;
  }>;
  templateLookup: Array<{
    value: string;
    label: string;
  }>;
}

interface SearchRequest {
  labId: number;
  reqTypeId: number;
  templateName: string;
  translatorId: string;
}

const INITIAL_SORTING_TYPE = {
  clickedIconData: "labId",
  sortingOrder: "desc",
} as const;

const INITIAL_SEARCH_QUERY = {
  labId: 0,
  reqTypeId: 11,
  templateName: "",
  translatorId: "",
} as const;

const queryDisplayTagNames: StringRecord = {
  labId: "Lab",
  templateName: "Template Name",
  LabName: "Lab Name",
  translatorId: "PGX Translator",
} as const;

const REQ_TYPE_ID = 11;

export default function TemplateSetting() {
  const { t } = useLang();
  const isMobile = useIsMobile();

  const searchRef = useRef<any>(null);
  const isApiCallInProgress = useRef(false);
  const lastRequestParams = useRef<string>("");

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<IRows[]>([]);
  const [searchedTags, setSearchedTags] = useState<string[]>([]);
  const [triggerSearchData, setTriggerSearchData] = useState(false);
  const [buttonClicked, setButtonClicked] = useState<boolean>(false);
  const [sort, setSorting] = useState<SortingTypeI>(INITIAL_SORTING_TYPE);
  const [dropDownValues, setDropDownValues] = useState<DropDownValues>({
    referenceLab: [],
    translatorLookup: [],
    templateLookup: [],
  });
  const [searchRequest, setSearchRequest] =
    useState<SearchRequest>(INITIAL_SEARCH_QUERY);

  // Pagination states
  const {
    curPage,
    pageSize,
    total,
    totalPages,
    pageNumbers,
    nextPage,
    prevPage,
    showPage,
    setPageSize,
    setTotal,
    setCurPage,
  } = usePagination();

  // Single fetch path: initial load, paging, search trigger and sort changes
  useEffect(() => {
    loadGridData(true, false);
  }, [curPage, pageSize, triggerSearchData, sort]);

  // Debounce timer ref
  const debounceTimer = useRef<any>(null);

  type InputChangeEvent = HTMLInputElement | HTMLSelectElement;
  const onInputChangeSearch = (e: React.ChangeEvent<InputChangeEvent>) => {
    setSearchRequest({ ...searchRequest, [e.target.name]: e.target.value });
  };
  const resetSearch = () => {
    setSearchRequest(INITIAL_SEARCH_QUERY);
    setSorting(INITIAL_SORTING_TYPE);
    lastRequestParams.current = "";
    setButtonClicked(false);
    setCurPage(1);
    // Fetch is handled by the effect above once the reset state is applied
    setTriggerSearchData((prev) => !prev);
  };

  const TemplateLookupApi = () => {
    FileTemplateLookup()
      .then((res: AxiosResponse) => {
        setDropDownValues((prev: any) => ({
          ...prev,
          templateLookup: res?.data,
        }));
      })
      .catch((err: any) => {
        console.trace(err);
      });
  };

  const TranslateLookupApi = () => {
    TranslatorLookupApi()
      .then((res: AxiosResponse) => {
        setDropDownValues((prev) => ({
          ...prev,
          translatorLookup: res?.data,
        }));
      })
      .catch((err: any) => {
        console.trace(err);
      });
  };

  const LabLookupApi = () => {
    FacilityService.referenceLabLookup()
      .then((res: AxiosResponse) => {
        const referenceArray =
          res?.data?.data?.map((val: any) => ({
            value: val?.labId,
            label: val?.labDisplayName,
          })) || [];

        setDropDownValues((prev) => ({
          ...prev,
          referenceLab: referenceArray,
        }));
      })
      .catch((err: any) => {
        console.trace(err);
      });
  };

  const loadGridData = (
    loader: boolean,
    reset: boolean,
    sortingState?: any
  ) => {
    // Prevent concurrent API calls
    if (isApiCallInProgress.current) {
      return;
    }

    const trimmedSearchRequest = Object.fromEntries(
      Object.entries(searchRequest).map(([key, value]) => [
        key,
        typeof value === "string" ? value.trim() : value,
      ])
    );

    // Ensure queryModel has the correct structure with ReqTypeId
    const queryModel = reset
      ? INITIAL_SEARCH_QUERY
      : {
          labId: trimmedSearchRequest.labId || 0,
          templateName: trimmedSearchRequest.templateName || "",
          translatorId: trimmedSearchRequest.translatorId,
          LabName: trimmedSearchRequest.LabName || "",
          ReqTypeId: REQ_TYPE_ID,
        };

    const requestObj = {
      pageNumber: curPage,
      pageSize: pageSize,
      queryModel: queryModel,
      sortColumn: reset ? sortingState?.clickedIconData : sort?.clickedIconData,
      sortDirection: reset ? sortingState?.sortingOrder : sort?.sortingOrder,
    };

    if (loader) {
      setLoading(true);
    }

    GetAllTemplateSettings(requestObj)
      .then((res: AxiosResponse) => {
        setRows(res?.data?.data);
        setTotal(res?.data?.total);
      })
      .catch((err: any) => {
        console.trace(err, "err");
      })
      .finally(() => {
        setLoading(false);
        isApiCallInProgress.current = false;
      });
  };

  const handleAddTemplate = () => {
    setButtonClicked(true);
    setRows((prevRows: IRows[]) => [
      {
        labId: 0,
        templateName: "",
        rowStatus: true,
        templateId: 0,
        translatorId: "",
        isActive: true,
      },
      ...prevRows,
    ]);
  };

  const handleSort = (columnName: any) => {
    const currentOrder = searchRef.current.id;
    const newOrder = currentOrder === "asc" ? "desc" : "asc";
    searchRef.current.id = newOrder;

    setSorting({
      sortingOrder: newOrder,
      clickedIconData: columnName,
    });
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      // Clear debounce timer and trigger immediate search
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      loadGridData(true, false);
    }
  };

  const handleTagRemoval = (clickedTag: string) => {
    setSearchRequest((prev) => ({
      ...prev,
      [clickedTag]: (INITIAL_SEARCH_QUERY as any)[clickedTag],
    }));
  };

  useEffect(() => {
    const activeTags = Object.entries(searchRequest)
      .filter(([, value]) => value)
      .map(([key]) => key);
    setSearchedTags(activeTags);
  }, [searchRequest]);

  // reqTypeId is always set, so one tag means "no user filters". Reset only when the
  // user removes their last real tag (count drops to 1), not on mount (0 -> 1).
  const prevTagsLength = useRef(searchedTags.length);
  useEffect(() => {
    const hadUserTags = prevTagsLength.current > 1;
    prevTagsLength.current = searchedTags.length;
    if (hadUserTags && searchedTags.length === 1) resetSearch();
  }, [searchedTags.length]);

  useEffect(() => {
    LabLookupApi();
    TemplateLookupApi();
    TranslateLookupApi();
  }, []);

  return (
    <>
      <div className="app-content flex-column-fluid p-0">
        <div className="card-body py-2">
          <div className="d-flex gap-4 flex-wrap mb-1">
            {searchedTags.map((tag) =>
              tag === "reqTypeId" ? null : (
                <div
                  className="d-flex align-items-center cursor-pointer gap-1 p-2 rounded bg-light"
                  onClick={() => handleTagRemoval(tag)}
                  key={tag}
                >
                  <span className="fw-bold">
                    {t(queryDisplayTagNames[tag])}
                  </span>
                  <i className="bi bi-x"></i>
                </div>
              )
            )}
          </div>
          <div className="d-flex flex-wrap gap-2 justify-content-center justify-content-sm-between align-items-center mb-2 col-12 responsive-flexed-actions">
            <div className="d-flex gap-2 responsive-flexed-actions">
              <div className="d-flex align-items-center">
                <span className="fw-400 mr-3">{t("Records")}</span>
                <select
                  id={`IDResultDataPreconfTemplateSettingRecords`}
                  className="form-select w-125px h-33px rounded py-2"
                  data-kt-select2="true"
                  data-placeholder="Select option"
                  data-dropdown-parent="#kt_menu_63b2e70320b73"
                  data-allow-clear="true"
                  onChange={(e) => {
                    setPageSize(parseInt(e.target.value));
                  }}
                >
                  <option value="5">5</option>
                  <option value="10">10</option>
                  <option value="50" selected>
                    50
                  </option>
                  <option value="100">100</option>
                </select>
              </div>
              <div className="d-flex gap-2 gap-lg-3 justify-content-center justify-content-sm-start">
                <div className="mt-0">
                  <PermissionComponent
                    moduleName="PGX LIS"
                    pageName="Result File Pre-configuration"
                    permissionIdentifier="AddNewTemplate"
                  >
                    {({ pageName, moduleName, permissionIdentifier }) => (
                      <button
                        id={`IDResultDataPreconfTemplateSettingAddNew`}
                        onClick={handleAddTemplate}
                        className="btn btn-primary btn-sm fw-bold mr-sm-3  text-capitalize"
                        disabled={loading || buttonClicked}
                      >
                        <i style={{ fontSize: "16px" }} className="fa">
                          &#xf067;
                        </i>
                        {getPermissionDisplayName(
                          pageName,
                          moduleName,
                          permissionIdentifier,
                          "Add New Template"
                        )}
                      </button>
                    )}
                  </PermissionComponent>
                </div>
              </div>
            </div>
            <div className="d-flex align-items-center gap-2 gap-lg-3">
              <button
                id={`IDResultDataPreconfTemplateSettingSearch`}
                onClick={() => {
                  setCurPage(1);
                  setTriggerSearchData((prev) => !prev);
                }}
                className="btn btn-linkedin btn-sm fw-500 py-2 rounded-3"
                aria-controls="Search"
              >
                {t("Search")}
              </button>
              <button
                onClick={resetSearch}
                type="button"
                className="btn btn-secondary btn-sm btn-secondary--icon fw-bold py-2 rounded-3"
                id={`IDResultDataPreconfTemplateSettingReset`}
              >
                <span>
                  <span>{t("Reset")}</span>
                </span>
              </button>
            </div>
          </div>
          <div className="card">
            <Box sx={{ height: "auto", width: "100%" }}>
              <div className="table_bordered overflow-hidden">
                <TableContainer
                  sx={
                    isMobile
                      ? {
                          overflowY: "hidden",
                        }
                      : {}
                  }
                  className="shadow-none"
                >
                  <Table
                    aria-label="sticky table collapsible"
                    className="table table-cutome-expend table-bordered table-sticky-header table-head-2-bg table-bg table-head-custom table-vertical-center border-0 mb-0"
                  >
                    <TableHead>
                      <TableRow className="h-40px">
                        <TableCell></TableCell>
                        <TableCell className="min-w-50px"></TableCell>
                        <TableCell>
                          <input
                            id={`IDResultDataPreconfTemplateSettingTemplateName`}
                            type="text"
                            name="templateName"
                            className="form-control bg-white rounded-2 fs-8 h-30px"
                            placeholder={t("Search ...")}
                            value={searchRequest.templateName}
                            onChange={onInputChangeSearch}
                            onKeyDown={handleKeyPress}
                          />
                        </TableCell>
                        <TableCell>
                          <Select
                            inputId={`PGXPreconfTemplateSettingLabSelect`}
                            menuPortalTarget={document.body}
                            styles={reactSelectSMStyle}
                            placeholder={t("Select ...")}
                            theme={(theme: any) => styles(theme)}
                            options={dropDownValues?.referenceLab}
                            name="labId"
                            onChange={(event: any) => {
                              setSearchRequest({
                                ...searchRequest,
                                labId: event.value,
                              });
                            }}
                            value={dropDownValues?.referenceLab.filter(
                              function (option: any) {
                                return option.value === searchRequest.labId;
                              }
                            )}
                          />
                        </TableCell>
                        <TableCell>
                          <Select
                            inputId={`IDResultDataPreconfTemplateSettingLabSelect`}
                            menuPortalTarget={document.body}
                            styles={reactSelectSMStyle}
                            placeholder={t("Select ...")}
                            theme={(theme: any) => styles(theme)}
                            options={dropDownValues?.translatorLookup}
                            name="translatorId"
                            onChange={(event: any) => {
                              setSearchRequest({
                                ...searchRequest,
                                translatorId: event.value,
                              });
                            }}
                            value={dropDownValues?.translatorLookup.filter(
                              function (option: any) {
                                return (
                                  option.value === searchRequest.translatorId
                                );
                              }
                            )}
                          />
                        </TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                      <TableRow className="h-30px">
                        <TableCell className="w-20px min-w-20px"></TableCell>
                        <TableCell className="w-50px min-w-50px text-center">
                          {t("Actions")}
                        </TableCell>
                        <TableCell
                          className="min-w-300px w-300px"
                          sx={{ width: "max-content" }}
                        >
                          <div
                            onClick={() => handleSort("templateName")}
                            className="d-flex justify-content-between cursor-pointer"
                            id=""
                            ref={searchRef}
                          >
                            <div style={{ width: "max-content" }}>
                              {t("Template Name")}
                            </div>

                            <div className="d-flex justify-content-center align-items-center mx-4 mr-0">
                              <ArrowUp
                                CustomeClass={`${
                                  sort.sortingOrder === "desc" &&
                                  sort.clickedIconData === "templateName"
                                    ? "text-success fs-7"
                                    : "text-gray-700 fs-7"
                                }  p-0 m-0 "`}
                              />
                              <ArrowDown
                                CustomeClass={`${
                                  sort.sortingOrder === "asc" &&
                                  sort.clickedIconData === "templateName"
                                    ? "text-success fs-7"
                                    : "text-gray-700 fs-7"
                                }  p-0 m-0`}
                              />
                            </div>
                          </div>
                        </TableCell>
                        <TableCell
                          className="min-w-300px w-300px"
                          sx={{ width: "max-content" }}
                        >
                          <div
                            onClick={() => handleSort("labName")}
                            className="d-flex justify-content-between cursor-pointer"
                            id=""
                            ref={searchRef}
                          >
                            <div style={{ width: "max-content" }}>
                              {t("Lab")}
                            </div>

                            <div className="d-flex justify-content-center align-items-center mx-4 mr-0">
                              <ArrowUp
                                CustomeClass={`${
                                  sort.sortingOrder === "desc" &&
                                  sort.clickedIconData === "labName"
                                    ? "text-success fs-7"
                                    : "text-gray-700 fs-7"
                                }  p-0 m-0 "`}
                              />
                              <ArrowDown
                                CustomeClass={`${
                                  sort.sortingOrder === "asc" &&
                                  sort.clickedIconData === "labName"
                                    ? "text-success fs-7"
                                    : "text-gray-700 fs-7"
                                }  p-0 m-0`}
                              />
                            </div>
                          </div>
                        </TableCell>
                        <TableCell
                          className="min-w-300px w-300px"
                          sx={{ width: "max-content" }}
                        >
                          <div
                            onClick={() => handleSort("translatorName")}
                            className="d-flex justify-content-between cursor-pointer"
                            id=""
                            ref={searchRef}
                          >
                            <div style={{ width: "max-content" }}>
                              {t("PGX Translator")}
                            </div>

                            <div className="d-flex justify-content-center align-items-center mx-4 mr-0">
                              <ArrowUp
                                CustomeClass={`${
                                  sort.sortingOrder === "desc" &&
                                  sort.clickedIconData === "translatorName"
                                    ? "text-success fs-7"
                                    : "text-gray-700 fs-7"
                                }  p-0 m-0 "`}
                              />
                              <ArrowDown
                                CustomeClass={`${
                                  sort.sortingOrder === "asc" &&
                                  sort.clickedIconData === "translatorName"
                                    ? "text-success fs-7"
                                    : "text-gray-700 fs-7"
                                }  p-0 m-0`}
                              />
                            </div>
                          </div>
                        </TableCell>
                        <TableCell
                          className="min-w-100px w-100px"
                          sx={{ width: "max-content" }}
                        >
                          <div className="d-flex justify-content-between">
                            <div style={{ width: "max-content" }}>
                              {t("Status")}
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading ? (
                        <TableCell colSpan={9} className="padding-0">
                          <Loader />
                        </TableCell>
                      ) : !rows.length ? (
                        <NoRecord message={"No data found for this table"} />
                      ) : (
                        rows.map((item: any, index: any) => {
                          return (
                            <Row
                              row={item}
                              rows={rows}
                              index={index}
                              setRows={setRows}
                              key={item.templateId}
                              loadGridData={loadGridData}
                              dropDownValues={dropDownValues}
                              setButtonClicked={setButtonClicked}
                              queryDisplayTagNames={queryDisplayTagNames}
                            />
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>
            </Box>
          </div>

          <CustomPagination
            curPage={curPage}
            nextPage={nextPage}
            pageNumbers={pageNumbers}
            pageSize={pageSize}
            prevPage={prevPage}
            showPage={showPage}
            total={total}
            totalPages={totalPages}
          />
        </div>
      </div>
    </>
  );
}
